import { ROTA_APIS } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import AxiosCurlirize from "axios-curlirize";

AxiosCurlirize(axios, () => {});

export interface IBookingFeeConfigBody {
  clinic_id: string[];
  sub_service_id: string[];
  day_of_week: string;
  booking_fee_amount: number;
  advance_days_for_booking_fee_applicability: number[];
  vms_on_off_status: boolean;
  app_on_off_status: boolean;
  included_user_persona: string;
  excluded_user_personas: string[];
  // Personas a customer must also hold, all of them. Accepted once the backend
  // reads booking fee configurations from v3.
  required_user_personas?: string[];
}

export interface IBookingFeeConfigResponse {
  status: boolean;
  message: string;
  data?: any;
}

export const useSaveBookingFeeConfigAPI = () => {
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const configuration = useConfiguration();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const execute = useCallback(async (body: IBookingFeeConfigBody) => {
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response = await axios
        .post(
          `${ROTA_APIS?.ROTA_BOOKING_FEE_CONFIG?.baseURL}`,
          body,
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((res: AxiosResponse<IBookingFeeConfigResponse>) => {
          if (res.data.status === true) {
            toast.success(res.data.message || "Booking Fee Configuration saved successfully!");
            // Rules that would leave some customers with a free slot by accident.
            const warnings: string[] = res.data.data?.warnings ?? [];
            if (warnings.length > 0) {
              toast(`Saved, but check: ${warnings.join(" | ")}`, { icon: "⚠️", duration: 10000 });
            }
            return { status: true, message: res.data.message };
          } else {
            toast.error(res.data.message || "Failed to save configuration.");
            return { status: false, message: res.data.message };
          }
        })
        .catch((e: AxiosError) => {
          sendCurl(e.config["curlCommand"]);
          captureError(e);
          const message =
            (e.response?.data as { message?: string } | undefined)?.message ||
            "Error saving booking fee configuration.";
          toast.error(message);
          return { status: false, message };
        });
      return response;
    } catch (e: any) {
      toast.error("Server Error: " + e.message);
      return { status: false, message: e.message };
    } finally {
      setIsLoading(false);
    }
  }, [configuration, sendCurl, captureError]);

  return { execute, isLoading };
};
